import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { documentsApi } from '@/api/documents.api';
import { envelopesApi, type EnvelopeView } from '@/api/envelopes.api';
import { type FolderView, foldersApi } from '@/api/folders.api';
import { AppShell } from '@/components/layout/AppShell';
import { RegistrySection } from '@/pages/dashboard/RegistryPage';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/format';

type LibraryMode = 'external' | 'internal' | 'registry' | 'archive';

/**
 * Document Library — the customer's "file server".
 *
 * Four views: External documents, Internal documents, the Document register
 * and the Archive. The two document views are the SAME folder tree, filtered
 * by the envelope's documentSource — browsing by numbering type was dropped
 * (patron: "By document type götürmək, ancaq folder üzrə saxlamaq").
 *
 * Permissions follow the same ACL as the envelope list:
 *   - owner / admin / manager → every completed envelope in the org
 *   - member / viewer         → only their own (creator or recipient)
 * No extra role gate on the page itself — anyone can open the Library
 * and see exactly what they're already authorised to see elsewhere.
 */
export function LibraryPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<LibraryMode>('external');

  const { data, isLoading } = useQuery({
    // Backs the tree + archive views only; the register section fetches its own.
    enabled: mode !== 'registry',
    queryKey: ['envelopes', 'library', mode === 'archive' ? 'archive' : 'active'],
    queryFn: () =>
      envelopesApi.list({
        status: mode === 'archive' ? undefined : 'completed',
        perPage: 200,
        archive: mode === 'archive',
        // Library is org-wide: every member sees every completed/archived
        // envelope regardless of whether they were on the routing list.
        library: true,
        // Keep declined documents out of the archive view too — they belong
        // only on the dashboard, not in any register/library listing.
        excludeDeclined: true,
      }),
  });

  const { data: folderData } = useQuery({
    queryKey: ['folders', 'list'],
    queryFn: () => foldersApi.list(),
  });

  // Documents created before `documentSource` existed carry no value. They are
  // treated as Internal so every document has exactly ONE home — showing them
  // under both tabs made External look like it contained internal paperwork.
  // The leaf still badges them "Source not set" so they stay easy to spot and
  // correct.
  const visibleEnvelopes = useMemo(() => {
    const items = data?.items ?? [];
    if (mode === 'archive') return items;
    return items.filter((e) =>
      mode === 'external' ? e.documentSource === 'External' : e.documentSource !== 'External',
    );
  }, [data, mode]);

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">{t('library.title')}</h1>
        <p className="page-subtitle">{t('library.subtitle')}</p>
      </div>

      <div className="flex gap-1 mb-4 flex-wrap">
        <ModeTab active={mode === 'external'} onClick={() => setMode('external')}>
          📤 {t('library.view_external')}
        </ModeTab>
        <ModeTab active={mode === 'internal'} onClick={() => setMode('internal')}>
          🏢 {t('library.view_internal')}
        </ModeTab>
        <ModeTab active={mode === 'registry'} onClick={() => setMode('registry')}>
          📋 {t('library.view_registry')}
        </ModeTab>
        <ModeTab active={mode === 'archive'} onClick={() => setMode('archive')}>
          🗄 {t('library.view_archive')}
        </ModeTab>
      </div>

      {mode === 'archive' && (
        <div className="card !p-3 mb-4 border-l-4 border-warning bg-warning-bg/40 flex gap-3 items-start">
          <div className="text-xl">⚠</div>
          <div className="text-[13px] text-gray-900 flex-1">
            <strong>{t('library.archive_banner_title')}</strong>
            <div className="text-[12px] text-muted mt-0.5">
              {t('library.archive_banner_hint')}
            </div>
          </div>
        </div>
      )}

      {mode === 'registry' ? (
        <RegistrySection />
      ) : isLoading ? (
        <div className="card text-center py-12 text-muted">{t('common.loading')}</div>
      ) : (data?.items.length ?? 0) === 0 ? (
        <div className="card text-center py-16">
          <div className="text-3xl mb-2">📂</div>
          <div className="font-medium text-gray-900">{t('library.empty_title')}</div>
          <div className="text-[13px] text-muted mt-1">{t('library.empty_text')}</div>
        </div>
      ) : (
        <FolderTreeView
          envelopes={visibleEnvelopes}
          folders={folderData?.folders ?? []}
          // The archive spans both sources, so its folder counts stay absolute;
          // a source-filtered view would report misleading totals.
          showFolderTotals={mode === 'archive'}
        />
      )}
    </AppShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Folder-tree view: user-defined folder buckets
// ──────────────────────────────────────────────────────────────────────────

function FolderTreeView({
  envelopes,
  folders,
  showFolderTotals,
}: {
  envelopes: EnvelopeView[];
  folders: FolderView[];
  /** Show "shown/total" badges. Off when the list is filtered by source, where
   *  the folder's absolute count would not match what the user can see. */
  showFolderTotals?: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Group: folder name → envelopes. "—" is the literal "Unfiled" bucket.
  const grouped = useMemo(() => {
    const map = new Map<string, { name: string; color: string; total: number; envelopes: EnvelopeView[] }>();
    // Seed every folder that holds at least one document (any status) so a
    // folder still shows here even when none of its documents are completed —
    // otherwise folders that appear in the Register column go missing in the
    // library (which lists completed documents only).
    for (const f of folders) {
      if (f.count > 0) {
        map.set(f.id, { name: f.name, color: f.color || '#9CA3AF', total: f.count, envelopes: [] });
      }
    }
    for (const env of envelopes) {
      const key = env.folder?.id ?? '__unfiled__';
      const name = env.folder?.name ?? '__unfiled__';
      const color = env.folder ? map.get(env.folder.id)?.color ?? '#9CA3AF' : '#D1D5DB';
      if (!map.has(key)) map.set(key, { name, color, total: 0, envelopes: [] });
      map.get(key)!.envelopes.push(env);
    }
    // Sort alphabetically, but pin "unfiled" to the bottom.
    return Array.from(map.entries()).sort(([ka, va], [kb, vb]) => {
      if (ka === '__unfiled__') return 1;
      if (kb === '__unfiled__') return -1;
      return va.name.localeCompare(vb.name);
    });
  }, [envelopes, folders]);

  if (grouped.length === 0) {
    return (
      <div className="card text-center py-12 text-muted text-[13px]">
        {t('library.empty_text')}
      </div>
    );
  }

  return (
    <div className="card !p-0">
      <ul className="divide-y divide-border">
        {grouped.map(([key, group]) => {
          const open = expanded.has(key);
          return (
            <li key={key}>
              <button
                onClick={() =>
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  })
                }
                className="w-full flex items-center gap-2 py-2.5 px-4 hover:bg-gray-50 text-left"
              >
                <span className="text-muted text-[11px] w-3">{open ? '▼' : '▶'}</span>
                <span
                  className="w-3 h-3 rounded-full inline-block"
                  style={{ background: group.color }}
                />
                <span className="font-medium text-gray-900">
                  {key === '__unfiled__' ? t('envelopes_list.folder_none') : group.name}
                </span>
                <span className="text-[11px] text-muted ml-auto">
                  {showFolderTotals && group.total > group.envelopes.length
                    ? `${group.envelopes.length}/${group.total}`
                    : group.envelopes.length}
                </span>
              </button>
              {open && (
                group.envelopes.length === 0 ? (
                  <div className="bg-gray-50/50 text-[12px] text-muted px-4 py-3" style={{ paddingLeft: 42 }}>
                    {t('library.folder_no_completed', 'No completed documents in this folder yet.')}
                  </div>
                ) : (
                  <ul className="divide-y divide-border bg-gray-50/50">
                    {group.envelopes
                      .slice()
                      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                      .map((env) => (
                        <EnvelopeLeaf key={env.id} env={env} indent={42} />
                      ))}
                  </ul>
                )
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Shared bits
// ──────────────────────────────────────────────────────────────────────────

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded-md text-[13px] font-medium',
        active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      )}
    >
      {children}
    </button>
  );
}

function EnvelopeLeaf({ env, indent }: { env: EnvelopeView; indent: number }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-3 py-2.5 pr-4" style={{ paddingLeft: indent }}>
      <span className="text-muted text-[12px]">📄</span>
      <div className="flex-1 min-w-0">
        <Link
          to={`/envelopes/${env.id}`}
          className="font-medium text-gray-900 truncate hover:text-primary no-underline block"
        >
          {env.title}
        </Link>
        <div className="text-[11px] text-muted">
          {env.documentNumber && (
            <code className="bg-white px-1 rounded mr-2">{env.documentNumber}</code>
          )}
          {formatDate(env.completedAt ?? env.createdAt)}
          {/* Pre-dates the Internal/External field — flagged rather than hidden,
              since such a document shows up under both source tabs. */}
          {!env.documentSource && (
            <span className="badge badge-muted ml-2 !text-[10px]">
              {t('library.source_unclassified', 'Source not set')}
            </span>
          )}
        </div>
      </div>
      {env.documents[0] && (
        <button
          onClick={() =>
            documentsApi.downloadSigned(
              env.documents[0].id,
              `${env.documents[0].originalName.replace(/\.pdf$/i, '')}__signed.pdf`,
            )
          }
          className="btn btn-ghost btn-sm"
          title={t('envelope_detail.download_signed')}
        >
          ⬇ PDF
        </button>
      )}
      <Link
        to={`/envelopes/${env.id}`}
        className="btn btn-secondary btn-sm"
        title={t('library.open_detail_hint')}
      >
        {t('common.open')}
      </Link>
    </li>
  );
}

